import { isInMonaco, type LatLng } from '../geo/monaco';
import { RideStatus } from '../enums/status';
import type { Ride } from './index';

/**
 * Signalements d'exploitation portés par une course.
 *
 * Ces alertes ne sont pas des erreurs : une course hors Principauté est
 * parfaitement légitime — un transfert vers l'aéroport de Nice en est une — mais
 * elle n'engage pas le même chauffeur ni la même durée. De même, une course que
 * plusieurs chauffeurs ont refusée finira par ne plus trouver personne. Dans les
 * deux cas, l'exploitation doit la voir **avant** que le client ne s'impatiente.
 */
export enum RideWarning {
	/** Prise en charge ou destination hors des limites de la Principauté. */
	OUTSIDE_MONACO = 'OUTSIDE_MONACO',
	/** Refusée par un nombre inhabituel de chauffeurs. */
	MANY_DECLINES = 'MANY_DECLINES',
	/** Plus aucun chauffeur joignable : la course est en souffrance. */
	NO_DRIVER = 'NO_DRIVER',
}

/**
 * Nombre de refus au-delà duquel une course est signalée, par défaut.
 *
 * Ajustable en exploitation (`declineWarningThreshold`) : la valeur ci-dessous
 * n'est que le point de départ.
 *
 * Quatre, soit une vague complète : que les quatre chauffeurs les plus proches
 * déclinent reste un aléa ordinaire. C'est le refus suivant qui trahit un
 * problème — adresse peu claire, destination lointaine, note dissuasive — et
 * mérite un œil humain.
 */
export const RIDE_DECLINE_WARNING_THRESHOLD = 4;

/** Courses encore en quête d'un chauffeur : les seules que l'on signale. */
const UNASSIGNED_STATUSES: RideStatus[] = [
	RideStatus.DRAFT,
	RideStatus.SCHEDULED,
	RideStatus.SEARCHING,
	RideStatus.NO_DRIVER,
];

/** Vrai si l'un des deux points sort de la Principauté. */
export function isOutsideMonaco(pickup: LatLng, dropoff?: LatLng | null): boolean {
	return !isInMonaco(pickup) || (!!dropoff && !isInMonaco(dropoff));
}

/**
 * Signalements applicables à une course, à cet instant.
 *
 * Rien n'est remonté dès qu'un chauffeur a accepté : la course a trouvé preneur,
 * l'alerte n'appelle plus d'action et laisserait, à rester affichée, une liste
 * rouge en permanence — où plus personne ne distingue ce qui compte.
 */
export function rideWarnings(
	ride: Ride,
	declineThreshold: number = RIDE_DECLINE_WARNING_THRESHOLD,
): RideWarning[] {
	if (ride.driverId != null || !UNASSIGNED_STATUSES.includes(ride.status)) {
		return [];
	}

	const warnings: RideWarning[] = [];

	if (ride.outsideZone) {
		warnings.push(RideWarning.OUTSIDE_MONACO);
	}
	if (ride.declinedCount > declineThreshold) {
		warnings.push(RideWarning.MANY_DECLINES);
	}
	if (ride.status === RideStatus.NO_DRIVER) {
		warnings.push(RideWarning.NO_DRIVER);
	}

	return warnings;
}

export const hasRideWarning = (ride: Ride, declineThreshold?: number): boolean =>
	rideWarnings(ride, declineThreshold).length > 0;

/** Libellés d'exploitation, en français — back-office uniquement. */
export const RIDE_WARNING_LABELS: Record<RideWarning, string> = {
	[RideWarning.OUTSIDE_MONACO]: 'Hors Principauté',
	[RideWarning.MANY_DECLINES]: 'Refusée plusieurs fois',
	[RideWarning.NO_DRIVER]: 'Sans chauffeur',
};

export const RIDE_WARNING_HINTS: Record<RideWarning, string> = {
	[RideWarning.OUTSIDE_MONACO]:
		'La prise en charge ou la destination sort des limites de Monaco.',
	[RideWarning.MANY_DECLINES]:
		`Plus de ${RIDE_DECLINE_WARNING_THRESHOLD} chauffeurs ont décliné cette course.`,
	[RideWarning.NO_DRIVER]: 'Toutes les vagues de diffusion se sont épuisées sans réponse.',
};
